import { formatAllMessages, formatCommunicationLogsMessages, formatInboxCombinedMessages, formatLeadModalChatMessages, formatMessages } from './messagesReducer/formatMessages.js';

import { createReducer } from 'lib/utilities.js';
import mergeByProp from '../../lib/mergeByProp.js';
import standardizeMessage from '../../lib/standardizeMessage.js';
import getEventFolders from 'lib/getEventFolders.js';
import { formatDateForDatabase } from 'components/LeadDataTodoEditor.js';
import { formatDate } from 'lib/dateUtilities.js';

const initialState = {
  allMessages: [],
  messageCounts: {},
  messageOffsets: {},
  communicationsScreenLoader: false,
  messageTotals: {
    leadModalChat: {},
  },
};

export default createReducer(initialState, {
  SET_COMMUNICATIONS_SCREEN_LOAD_STATE: (state, payload) => ({
    ...state,
    communicationsScreenLoader: payload,
  }),
  GET_MESSAGES_FOR_COMMUNICATION_LOGS_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.filter((n) => n.data_source !== 'communicationLogs').concat(formatCommunicationLogsMessages(payload.data))),
  }),

  GET_PAGINATED_MESSAGES_FOR_COMMUNICATION_LOGS_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.concat(formatCommunicationLogsMessages(payload.data))),
  }),

  GET_MESSAGE_COUNTS_FOR_INBOX_SUCCESS: (state, payload) => ({
    ...state,
    messageCounts: {
      ...state.messageCounts,
      [(payload.meta || {}).folderId]: (payload.data || {}).total || 0,
    },
  }),

  GET_MESSAGES_FOR_INBOX_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.filter((n) => n.data_source !== `inbox_${(payload.meta || {}).folderId}` || n.rehydrated === true).concat(formatInboxCombinedMessages(payload.data, (payload.meta || {}).folderId, payload))),
    messageCounts: {
      ...state.messageCounts,
      [(payload.meta || {}).folderId]: ((payload.data || [])[0] || {}).total_row_count || 0,
    },
    messageOffsets: {
      ...state.messageOffsets,
      [(payload.meta || {}).folderId]: 0,
    },
  }),

  GET_MESSAGE_FOR_INBOX_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.concat(formatInboxCombinedMessages(payload.data, (payload.meta || {}).folderId).map((n) => ({ ...n, rehydrated: true })))),
    messageCounts: {
      ...state.messageCounts,
      [(payload.meta || {}).folderId]: ((payload.data || [])[0] || {}).total_row_count || 0,
    },
  }),

  GET_PAGINATED_MESSAGES_FOR_INBOX_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.concat(formatInboxCombinedMessages(payload.data, (payload.meta || {}).folderId, payload))),
    messageCounts: {
      ...state.messageCounts,
      [(payload.meta || {}).folderId]: ((payload.data || [])[0] || {}).total_row_count || 0,
    },
  }),

  INBOX_SET_OFFSET: (state, { folderId, offset }) => ({
    ...state,
    messageOffsets: {
      ...state.messageOffsets,
      [folderId]: offset,
    },
  }),

  INBOX_INCREMENT_OFFSET: (state, { folderId, amount }) => ({
    ...state,
    messageOffsets: {
      ...state.messageOffsets,
      [folderId]: (state.messageOffsets[folderId] || 0) + amount,
    },
  }),

  GET_REHYDRATED_MESSAGE_FOR_INBOX_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.concat(formatInboxCombinedMessages(payload.data, (payload.meta || {}).folderId, payload).map((n) => ({ ...n, rehydrated: true })))),
  }),

  GET_MESSAGE_FOR_CHAT_LEADS_SUCCESS: (state, payload) => {
    const formattedMessage = formatLeadModalChatMessages([payload.data])[0] || {};
    return {
      ...state,
      allMessages: formatAllMessages(state.allMessages.filter((n) => !(n.unique_id === formattedMessage.unique_id && n.data_source === 'leadModalChat')).concat(formattedMessage)),
    };
  },

  GET_MESSAGES_FOR_CHAT_LEADS_SUCCESS: (state, payload) => {
    return {
      ...state,
      allMessages: formatAllMessages(state.allMessages.filter((n) => n.data_source !== 'leadModalChat').concat(formatLeadModalChatMessages(payload.data.communications || []))),
      messageTotals: {
        ...state.messageTotals,
        leadModalChat: Object.fromEntries(Object.entries(payload.data.counts).map((n) => [n[0], parseFloat(n[1])])),
      },
    };
  },

  GET_PAGINATED_MESSAGES_FOR_CHAT_LEADS_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.concat(formatLeadModalChatMessages(payload.data.communications || []))),
  }),

  GET_EVENT_THREAD_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.filter((n) => n.data_source !== 'leadModalChat').concat(formatLeadModalChatMessages(payload.data || []))),
  }),

  PATCH_MESSAGE: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.map((n) => (n.unique_id === payload.unique_id ? { ...n, ...payload } : n))),
  }),

  GET_UPDATED_MESSAGE_EVENT: (state, payload) => {
    const allMessages = formatAllMessages(
      mergeByProp('unique_id', state.allMessages, formatMessages([{ ...payload.event.message }]), (existingMessage, newMessage) => ({
        ...newMessage,
        data_source: existingMessage.data_source,
        ...(existingMessage.attachments ? { attachments: existingMessage.attachments } : {}),
      }))
    );

    return {
      ...state,
      allMessages,
    };
  },

  PATCH_MESSAGE_AS_READ: (state, { uniqueId }) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.map((n) => (n.unique_id === uniqueId ? { ...n, is_read: 1 } : n))),
  }),

  PATCH_MESSAGE_AS_UNREAD: (state, { uniqueId }) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.map((n) => (n.unique_id === uniqueId ? { ...n, is_read: 0 } : n))),
  }),

  PATCH_MESSAGE_FOR_INBOX: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages(state.allMessages.map((n) => (n.unique_id === payload.unique_id && n.data_source.includes('inbox_') ? { ...n, ...formatInboxCombinedMessages([payload], n.data_source.split('_')[1])[0] } : n))),
  }),

  DELETE_MESSAGES_BY_DATA_SOURCE: (state, dataSource) => ({
    ...state,
    allMessages: state.allMessages.filter((n) => !dataSource.includes(n.data_source)),
  }),

  DELETE_MESSAGE_BY_DATA_SOURCE: (state, { uniqueId, dataSource }) => ({
    ...state,
    allMessages: state.allMessages.filter((n) => !((n.data_source || '').includes(dataSource) && n.unique_id === uniqueId)),
  }),

  PATCH_THREAD_META_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: state.allMessages.map((n) => ({
      ...n,
      thread_meta:
        n.thread_id === (payload.data || {}).thread_id || n.id === (payload.data || {}).event_id //
          ? payload.data
          : n.thread_meta,
    })),
  }),

  POST_CALL_NOTE_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: state.allMessages.map((n) => (n.id === payload.data.id ? { ...n, event_notes: payload.data.event_notes } : n)),
  }),

  GET_REVIEW_LOOP_MESSAGE_STATUS_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: state.allMessages.map((n) => {
      return n.message_id !== payload.data.data.hash ? n : { ...n, status: payload.data.data.status, reviewloop_data: payload.data.data };
    }),
  }),

  GET_UPDATED_INBOX_COUNT: (state, payload) => {
    return {
      ...state,
    };
  },

  POST_DRAFT_EMAIL_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages([...state.allMessages.filter((n) => n.id !== payload.data.deleted_draft_email_id), ...formatLeadModalChatMessages([payload.data])]),
  }),

  SET_MESSAGE_HIDDEN: (state, payload) => ({
    ...state,
    allMessages: state.allMessages.map((n) => (!n.data_source.includes('inbox_') || payload.message.unique_id !== n.unique_id ? n : { ...n, hidden: payload.hidden })),
  }),

  PATCH_TODO_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: state.allMessages.map((n) => (n.id === payload.data.id ? { ...n, ...payload.data, type: n.type, data_source: n.data_source } : n)),
  }),

  POST_COMPOSER_MESSAGE_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages([...state.allMessages, ...formatLeadModalChatMessages([{ ...payload.data, type: payload.data.type || 'todos' }])]),
  }),

  POST_PHONE_CALL_SUCCESS: (state, payload) => ({
    ...state,
    allMessages: formatAllMessages([...state.allMessages, ...formatLeadModalChatMessages([payload.data])]),
  }),

  PATCH_CALL_NOTE: (state, payload) => ({
    ...state,
    allMessages: state.allMessages.map((n) => (n.type === 'phone_calls' && n.id === payload.id ? { ...n, event_notes: payload.event_notes } : n)),
  }),

  DELETE_DRAFT: (state, payload = {}) => {
    const returnObject = state.allMessages.filter((n) => n.id !== payload.emailId);
    return {
      ...state,
      allMessages: returnObject,
    };
  },

  GET_UPDATED_LEAD_CREATION: (state, payload = {}) => {
    const data = payload.event.event_data && !payload.event.message ? payload.event.event_data : payload.event.message;
    const id = data.id;
    const processedMessages = (state.allMessages || []).map((n) => (n.id === id && n.data_source.includes('inbox_') ? { ...n, data_source: null } : n));
    return {
      ...state,
      allMessages: processedMessages,
    };
  },

  GET_EVENT: (state, payload) => {
    const serviceProviderId = payload.serviceProviderId;
    const folders = payload.folders;
    const message = { ...standardizeMessage((payload.event.data || {}).event_data) };
    if (!message.id && !message.lead_id) {
      return state;
    }
    if (message.service_provider_id !== serviceProviderId) {
      return state;
    }

    const eventFolders = getEventFolders(folders, payload.event.data || {});
    const dataSources = eventFolders.map((n) => `inbox_${n}`);
    const chatMessage = state.allMessages.find((n) => n.data_source === 'leadModalChat');
    const chatMatch = chatMessage && ((message.lead_id && chatMessage.lead_id === message.lead_id) || (message.thread_id && chatMessage.thread_id === message.thread_id));
    if (chatMatch) {
      dataSources.push('leadModalChat');
    }

    if (payload.fixture) {
      message.id = String(Math.floor(Math.random() * 1000000)) + 'cow';
      message.unique_id = `${String(message.id)}_${message.type}`;
      message.full_name = `${message.full_name} (Fixture)`;
      message.last_name = `${message.last_name} (Fixture)`;
      message.creationDatetime = '2031-01-01 00:00:00';
      message.creation_datetime = message.creationDatetime;
    }

    const messages = dataSources.map((dataSource) => formatMessages([message], { dataSource })[0]);
    return {
      ...state,
      allMessages: formatAllMessages([...state.allMessages, ...messages]),
    };
  },

  PUT_NOTE_SUCCESS: (state, payload) => {
    return {
      ...state,
      allMessages: state.allMessages.map((n) => (n.type === 'notes' && n.id === payload.data.id ? { ...n, note: payload.data.note } : n)),
    };
  },
});
